import { useCallback } from 'react';
import { useRelayEnvironment, type GraphQLTaggedNode } from 'react-relay';
import { useApolloClient } from '@apollo/react-hooks';
import {
	type IEnvironment,
	type ReaderSelection,
	type ReaderLinkedField,
	type ReaderScalarField,
	type ReaderArgument,
	type ReaderFragment,
} from 'relay-runtime';

import { toDocumentNode } from './toDocumentNode.ts';

export type UseWriteFragmentInteropArgs = {
	fragmentSpec: GraphQLTaggedNode;
	/** Fragment artifact for adding __typename to data. __typename will be automatically added. */
	fragmentArtifact: ReaderFragment;
};

export type UseWriteFragmentInteropResult<TData> = (params: {
	fragmentId: string;
	data: Omit<WithTypename<TData>, ' $fragmentType'>;
	variables?: Record<string, any>;
	useAggClient?: boolean;
}) => void;

/**
 * Utility type that represents data with __typename added at all levels.
 * This is a recursive type that adds __typename to objects and their nested object properties.
 */
export type WithTypename<T> =
	T extends Array<infer U>
		? Array<WithTypename<U>>
		: T extends Record<string, any>
			? { [K in keyof T]: WithTypename<T[K]> } & { __typename?: string }
			: T;

/**
 * Recursively processes fragment selections to add __typename to nested linked fields
 */
const addTypenameToSelections = (
	data: Record<string, any>,
	selections: readonly ReaderSelection[],
): void => {
	if (!data || typeof data !== 'object') {
		return;
	}

	for (const selection of selections) {
		if (selection.kind === 'LinkedField') {
			const fieldData = data[selection.name];

			if (fieldData && typeof fieldData === 'object') {
				if (Array.isArray(fieldData)) {
					// Handle arrays of linked records
					for (const item of fieldData) {
						if (item && typeof item === 'object') {
							// Add __typename using concreteType if available
							if (selection.concreteType && !('__typename' in item)) {
								item.__typename = selection.concreteType;
							}
							// Recursively process nested selections
							if (selection.selections) {
								addTypenameToSelections(item, selection.selections);
							}
						}
					}
				} else {
					// Handle single linked record
					// Add __typename using concreteType if available
					if (selection.concreteType && !('__typename' in fieldData)) {
						fieldData.__typename = selection.concreteType;
					}
					// Recursively process nested selections
					if (selection.selections) {
						addTypenameToSelections(fieldData, selection.selections);
					}
				}
			}
		} else if (selection.kind === 'InlineFragment') {
			// For inline fragments, add __typename if concreteType matches or is available
			if ((selection as any).type && !('__typename' in data)) {
				data.__typename = (selection as any).type;
			}
			// Recursively process inline fragment selections
			if (selection.selections) {
				addTypenameToSelections(data, selection.selections);
			}
		} else if (selection.kind === 'Condition') {
			// For conditional selections (@include/@skip), process nested selections
			if (selection.selections) {
				addTypenameToSelections(data, selection.selections);
			}
		}
	}
};

/**
 * Adds __typename properties to a data object using the Fragment artifact's type information.
 * This is useful for Apollo cache operations which require __typename fields.
 *
 * @param data - The data object to augment with __typename properties
 * @param fragmentArtifact - The ReaderFragment artifact containing type information
 * @returns A new data object with __typename properties added at each level
 */
export const addTypenameToData = <TData extends Record<string, any>>(
	data: TData,
	fragmentArtifact: ReaderFragment,
): WithTypename<TData> => {
	if (!data || typeof data !== 'object' || !fragmentArtifact) {
		return data as WithTypename<TData>;
	}

	// Create a shallow copy to avoid mutating the original
	const result = { ...data } as WithTypename<TData>;

	// Add __typename at root level using the fragment's type
	if (fragmentArtifact.type && !('__typename' in result)) {
		(result as any).__typename = fragmentArtifact.type;
	}

	// Process selections to add __typename to nested objects
	if (fragmentArtifact.selections) {
		addTypenameToSelections(result, fragmentArtifact.selections);
	}

	return result;
};

function useWriteFragmentInteropRelay<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<TData> {
	const { fragmentSpec } = args;
	const env = useRelayEnvironment();

	return useCallback(
		({ data, fragmentId, variables }) => {
			writeFragmentShim(env, fragmentSpec, fragmentId, data, variables);
		},
		[env, fragmentSpec],
	);
}

function useWriteFragmentInteropApollo<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<TData> {
	const { fragmentSpec, fragmentArtifact } = args;
	const apolloClient = useApolloClient();

	return useCallback(
		({ data, fragmentId, variables, useAggClient }) => {
			// Add __typename to data using the fragment artifact's type information
			const dataToWrite = addTypenameToData(
				data as Record<string, any>,
				fragmentArtifact,
			) as unknown as TData;

			// added the check in case this is used in a server component where window is not defined
			const client =
				useAggClient && typeof window !== 'undefined'
					? window.GLOBAL_APOLLO_AGG_CLIENT
					: apolloClient;

			client &&
				client.writeFragment<TData>({
					id: fragmentId,
					fragment: toDocumentNode(fragmentSpec),
					data: dataToWrite as TData,
					variables,
				});
		},
		[apolloClient, fragmentSpec, fragmentArtifact],
	);
}

export function useWriteFragmentInterop<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<Omit<TData, ' $fragmentType'>> {
	return process.env.BUILD_VARIANT === 'relay'
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useWriteFragmentInteropRelay(args)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useWriteFragmentInteropApollo(args);
}

function apolloFragmentIdToRelayFragmentId(apolloFragmentId: string) {
	return apolloFragmentId.replace(/^\$ROOT_QUERY\./, 'client:root:');
}

/**
 * Shim function that mimics Apollo's `writeFragment` using Relay
 */
function writeFragmentShim<TData>(
	environment: IEnvironment,
	fragment: GraphQLTaggedNode,
	fragmentId: string,
	data: TData,
	variables?: Record<string, any>,
): void {
	if (!data) {
		return;
	}

	const relayFragmentId = apolloFragmentIdToRelayFragmentId(fragmentId);

	// Use commitUpdate to write to the store
	environment.commitUpdate((storeProxy) => {
		const record = storeProxy.get(relayFragmentId);
		if (!record) {
			return;
		}

		// Helper to compute storage key for fields with arguments
		const getFieldKey = (
			field: ReaderLinkedField | ReaderScalarField,
			vars: Record<string, any> = {},
		): string => {
			if (!field.args || field.args.length === 0) {
				return field.name;
			}

			// Compute storage key for fields with arguments
			const args = field.args
				.map((arg: ReaderArgument) => {
					let value: string;
					if (arg.kind === 'Literal') {
						value = JSON.stringify(arg.value);
					} else if (arg.kind === 'Variable') {
						value = JSON.stringify(vars[arg.variableName]);
					} else {
						// Fallback for other argument types
						value = JSON.stringify((arg as any).value);
					}
					return `${arg.name}:${value}`;
				})
				.join(',');

			return `${field.name}(${args})`;
		};

		// Define a function to write the fields specified in the fragment
		const writeFields = (
			recordId: string,
			fields: readonly ReaderSelection[],
			writeData: any,
		): void => {
			const targetRecord = storeProxy.get(recordId);
			if (!targetRecord || !writeData) {
				return;
			}

			for (const field of fields) {
				if (field.kind === 'Condition') {
					// Handle @include/@skip directives with variables
					const conditionValue = variables?.[field.condition];
					const shouldInclude = field.passingValue ? conditionValue : !conditionValue;

					if (shouldInclude) {
						// Recursively process the selections within this condition
						writeFields(recordId, field.selections, writeData);
					}
				} else if (field.kind === 'LinkedField') {
					// For linked fields, we need to handle nested data
					const fieldKey = getFieldKey(field, variables ?? {});
					const linkedData = writeData[field.name];
					if (linkedData) {
						const linkedRecord = targetRecord.getLinkedRecord(fieldKey);
						if (linkedRecord) {
							writeFields(linkedRecord.getDataID(), field.selections, linkedData);
						}
					}
				} else if (field.kind === 'ScalarField') {
					const fieldKey = getFieldKey(field, variables ?? {});
					if (writeData[field.name] !== undefined) {
						targetRecord.setValue(writeData[field.name], fieldKey);
					}
				} else if (field.kind === 'InlineFragment') {
					// Handle inline fragments (e.g., ... on SomeType)
					// For write operations, we generally process all inline fragment selections
					writeFields(recordId, field.selections, writeData);
				} else {
					throw new Error(`Unsupported field kind: ${field.kind}`);
				}
			}
		};

		const writerFragment = fragment as any;
		const topLevelFields = writerFragment?.selections;

		if (topLevelFields) {
			writeFields(relayFragmentId, topLevelFields ?? [], data);
		}
	});
}
